/**
 * 메일 상세 뷰 — 네이버 메일·워크스 스타일.
 *
 * 레이아웃:
 *   ┌───────────────────────────────────────────────────────────┐
 *   │ ↩ 답장  ↩↩ 전체회신  → 전달   │ 🗑 삭제  ⚠ 스팸  ✉ 안읽음 │
 *   │ 📁 이동▾  ⋮ 더보기                              ★  ✕      │
 *   ├───────────────────────────────────────────────────────────┤
 *   │ ☆ Re: 제목                                                 │
 *   │   ──────────────────                                        │
 *   │   ● 보낸이      홍길동 <hong@example.com>                    │
 *   │     받는이      ksw@gayasoft.in   test1@gayasoft.in         │
 *   │     참조        ksw@gayasoft.in                              │
 *   │     일시        2026년 5월 3일 (일) 01:45                     │
 *   ├───────────────────────────────────────────────────────────┤
 *   │ 본문 (HTML)                                                  │
 *   ├───────────────────────────────────────────────────────────┤
 *   │ 첨부 N 개 — [card] [card]                                    │
 *   └───────────────────────────────────────────────────────────┘
 *
 * 단일 툴바 (텍스트 + 아이콘 1행). 하단 중복 답장 버튼 제거.
 * padding 은 컴팩트(p: 2 / pl: 0).
 */
import React, { useState } from "react";
import {
    Box, Checkbox, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
    Divider, FormControlLabel, IconButton, Menu, MenuItem,
    Stack, Tooltip, Typography,
} from "@mui/material";
import ActionButton from "../../component/common/ActionButton";
import ReplyRoundedIcon          from "@mui/icons-material/ReplyRounded";
import ReplyAllRoundedIcon       from "@mui/icons-material/ReplyAllRounded";
import ForwardRoundedIcon        from "@mui/icons-material/ForwardRounded";
import StarRoundedIcon           from "@mui/icons-material/StarRounded";
import StarBorderRoundedIcon     from "@mui/icons-material/StarBorderRounded";
import DeleteOutlineRoundedIcon  from "@mui/icons-material/DeleteOutlineRounded";
import DeleteForeverRoundedIcon  from "@mui/icons-material/DeleteForeverRounded";
import FolderRoundedIcon         from "@mui/icons-material/FolderRounded";
import ScheduleSendRoundedIcon   from "@mui/icons-material/ScheduleSendRounded";
import SendRoundedIcon           from "@mui/icons-material/SendRounded";
import EditRoundedIcon           from "@mui/icons-material/EditRounded";
import ReportRoundedIcon         from "@mui/icons-material/ReportRounded";
import MoveToInboxRoundedIcon    from "@mui/icons-material/MoveToInboxRounded";
import CloseRoundedIcon          from "@mui/icons-material/CloseRounded";
import DownloadRoundedIcon       from "@mui/icons-material/DownloadRounded";
import ZoomInRoundedIcon         from "@mui/icons-material/ZoomInRounded";
import MarkEmailUnreadRoundedIcon from "@mui/icons-material/MarkEmailUnreadRounded";
import DriveFileMoveRoundedIcon  from "@mui/icons-material/DriveFileMoveRounded";
import PrintRoundedIcon          from "@mui/icons-material/PrintRounded";
import BlockRoundedIcon          from "@mui/icons-material/BlockRounded";
import DomainDisabledRoundedIcon from "@mui/icons-material/DomainDisabledRounded";
import LockOpenRoundedIcon       from "@mui/icons-material/LockOpenRounded";
import LinkRoundedIcon           from "@mui/icons-material/LinkRounded";
import OpenInNewRoundedIcon      from "@mui/icons-material/OpenInNewRounded";
import PersonAddAltRoundedIcon   from "@mui/icons-material/PersonAddAltRounded";
import dayjs                      from "dayjs";
import { mailApi }                from "./api/mailApi";
import { ds }                     from "../../styles/designSystem";
import { notify }                 from "../../util/toast";
import { confirm }                from "../../util/confirmDialog";
import MailBodyHtml               from "./components/compose/MailBodyHtml";
import { formatFileSize } from "../../util/numberFormat";
import ContactEditDialog          from "./components/contacts/ContactEditDialog";
import AttachmentPreviewModal     from "./components/AttachmentPreviewModal";
import DeliveryStatusBadge, { resolveDeliveryBadge } from "./components/DeliveryStatusBadge";

/* 미리보기 가능 여부 — 이미지·PDF·텍스트·오피스(pptx/docx/xls 등) */
const PREVIEWABLE_RE = /\.(pdf|png|jpe?g|gif|webp|bmp|svg|txt|csv|log|md|docx?|pptx?|xlsx?|odt|odp|ods|rtf)$/i;
function isAttPreviewable(att) {
    const m = att?.mimeType || "";
    if (m.includes("pdf") || m.startsWith("image/") || m.startsWith("text/")
        || m.includes("officedocument") || m.includes("msword")
        || m.includes("ms-powerpoint") || m.includes("ms-excel")
        || m.includes("opendocument")) return true;
    return PREVIEWABLE_RE.test(att?.filename || "");
}

/**
 * 첨부 다운로드 — 새 탭 GET 은 X-TOKEN 헤더가 안 실려 401. axios blob → 임시 a 태그 강제 다운로드.
 * Content-Disposition 헤더의 서버 파일명(한글 RFC 5987) 우선.
 */
async function saveAttachment(att) {
    try {
        const res = await mailApi.downloadAttachment(att.id);
        const blob = res?.data ?? res;
        const cd   = res?.headers?.["content-disposition"] || "";
        let name = att.filename || "attachment";
        const m1 = cd.match(/filename\*\s*=\s*UTF-8''([^;]+)/i);
        const m2 = cd.match(/filename\s*=\s*"([^"]+)"/i);
        if (m1) { try { name = decodeURIComponent(m1[1]); } catch { /* keep */ } }
        else if (m2) { try { name = decodeURIComponent(m2[1]); } catch { name = m2[1]; } }
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = name;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
    } catch (e) {
        notify(e?.message || "다운로드 실패", "error");
    }
}

/* ── 첨부 카드 ─ */
/**
 * 첨부 한 건 — [종류 아이콘] 파일명(굵게) / 용량, 오른쪽에 미리보기·다운로드.
 *
 * <p>폭은 파일명 길이만큼만 차지한다 — 예전의 {@code minWidth: 220} 고정폭을 뺐다. 헤더 메타 행에
 * 여러 건이 나란히 들어가야 하는데, 짧은 이름까지 220px 를 먹으면 두 건만 있어도 줄이 넘어갔다.
 */
function AttachmentCard({ att, onPreview }) {
    const isPdf = (att.mimeType || "").includes("pdf");
    const isImg = (att.mimeType || "").startsWith("image/");
    const icon  = isPdf ? "📕" : isImg ? "🖼️" : "📎";
    const previewable = isAttPreviewable(att);

    return (
        <Box sx={{
            display: "flex", alignItems: "center", gap: 0.9,
            border: "1px solid #e2e8f0", borderRadius: 1.2,
            px: 1, py: 0.5, maxWidth: "100%", minWidth: 0,
            "&:hover": { borderColor: ds.colors.primary, bgcolor: "#f8faff" },
            transition: "all 0.12s",
        }}>
            <Box sx={{ fontSize: 17, lineHeight: 1, flexShrink: 0 }}>{icon}</Box>
            <Box sx={{ minWidth: 0 }}>
                <Tooltip title={att.filename || ""} placement="top">
                    <Typography noWrap sx={{
                        fontSize: 12, fontWeight: 700, color: "#0f172a", lineHeight: 1.35,
                    }}>
                        {att.filename || "(이름 없음)"}
                    </Typography>
                </Tooltip>
                {/*
                  0바이트 첨부는 "빈 파일" 로 명시한다.
                  formatFileSize 는 0/null 을 빈 문자열로 돌려주므로(numberFormat.js) 그대로 두면
                  <b>크기가 아예 안 보여서</b> 멀쩡한 파일처럼 보인다. 실제로 사용자가 눌러보고 나서야
                  "지원되지 않는 형식" 을 만났다(실측: 아웃룩이 0바이트로 보낸 image001.jpg 10개).
                  숨기지 않는 이유 — 발신자가 무언가를 붙였다는 사실 자체가 정보이고,
                  그걸 알아야 재발송을 요청할 수 있다.
                */}
                <Tooltip title={att.sizeBytes ? "" : "원본 메일에 파일 내용이 들어있지 않습니다. 발신자에게 재발송을 요청해주세요."}>
                    <Typography sx={{
                        fontSize: 11, lineHeight: 1.3,
                        color: att.sizeBytes ? "#94a3b8" : "#b45309",
                    }}>
                        {formatFileSize(att.sizeBytes, "빈 파일")}
                    </Typography>
                </Tooltip>
            </Box>
            {/* 두 아이콘은 한 묶음 — 사이 간격을 최소로 (기본 IconButton 패딩이 벌어져 보였다). */}
            <Box sx={{ display: "flex", alignItems: "center", flexShrink: 0 }}>
                {previewable && onPreview && (
                    <Tooltip title="미리보기">
                        <IconButton size="small" onClick={() => onPreview(att)} sx={{ p: 0.25 }}>
                            <ZoomInRoundedIcon sx={{ fontSize: 16, color: "#64748b" }} />
                        </IconButton>
                    </Tooltip>
                )}
                <Tooltip title="다운로드">
                    <IconButton size="small" onClick={() => saveAttachment(att)} sx={{ p: 0.25 }}>
                        <DownloadRoundedIcon sx={{ fontSize: 16, color: "#64748b" }} />
                    </IconButton>
                </Tooltip>
            </Box>
        </Box>
    );
}

/* "Name <email>" 또는 "email" 형식의 표시 문자열 */
function formatAddress(a) {
    if (!a) return "";
    return a.displayName ? `${a.displayName} <${a.address}>` : a.address;
}

/**
 * 메타 영역(보낸이/받는이/참조/숨은참조/보낸일시) 라벨의 고정 폭.
 * 한글 글자수가 달라서 행마다 chip 시작점이 어긋나는 문제를 방지.
 */
const LABEL_W = 72;

/**
 * 발신자/도메인 차단 버튼을 노출할 폴더 — 수신 계열만.
 * <p>SENT/DRAFT/SCHEDULED 에서 노출하면 "내가 보낸 메일"의 발신자 = 본인 주소를 차단하게 된다.
 * 백엔드도 본인 주소·사내 도메인 차단은 400 으로 거부하므로 애초에 버튼을 숨긴다.
 */
const BLOCKABLE_FOLDERS = ["INBOX", "SPAM", "ARCHIVE", "CUSTOM", "TRASH"];

/**
 * TODO 배달상태 노출 범위 — 지금은 예약함·임시보관함에서만.
 * 목록 화면(MailPage.DELIVERY_BADGE_FOLDERS)과 같이 움직인다.
 *
 * <p>이 배지는 대부분의 메일에서 <b>"발송됨 — 발송 서버가 접수했습니다"</b> 에 머문다.
 * 배달완료로 넘어가려면 SES 배달 이벤트가 들어와야 하는데, 백업/아카이브에서 올린 메일에는 그 이벤트가
 * 애초에 없고 신규 발송분도 수집이 확인되지 않았다. 도착한 메일을 보면서 "접수했습니다" 를 읽는 건
 * 정보가 아니라 잡음이라 화면 노출만 잠갔다. <b>백엔드 수집·저장은 그대로 돈다</b>.
 *
 * <p><b>판정 기준이 {@code mail.direction} 이 아니라 폴더인 이유</b>(다시 켤 때도 유효):
 * 사내 A→B 메일은 From 이 사내 직원이라 메일 자체는 {@code direction=OUTBOUND} 지만, 받는 B 에게도
 * 정상적인 INBOX 상태행이 생긴다. 메일 행은 하나고 상태행만 사람마다 다르다. direction 으로 걸면
 * B 가 받은메일함에서 읽는데도 발신자용 배지가 떴다 — 실제로 그랬다.
 *
 * <p><b>DRAFT 를 남겨 둔 이유</b> — 발송이 깨지면 {@code status=FAILED} 인 채로 임시보관함에 남는다
 * (MailSendService.recordSendFailure. 예약 발송 실패도 SCHEDULED→DRAFT 로 옮겨진다).
 * 여기서 배지를 끄면 "왜 안 나갔는지" 를 볼 자리가 아예 없어진다. 상세는 {@code showHint} 로
 * 사유 원문(failureReason)까지 펼쳐 주므로 실패 진단이 실질적으로 여기서 이뤄진다.
 * 평범한 임시저장 메일은 {@code status=DRAFT} 라 배지 대상이 아니어서 아무것도 붙지 않는다.
 *
 * <p>다시 전면 노출하려면 {@code "SENT"} 를 넣으면 된다.
 */
const DELIVERY_BADGE_FOLDERS = ["SCHEDULED", "DRAFT"];

/* ── 주소 행: "받는이  [chip] [chip] [chip]" 형식 ─ */
function AddressLine({ label, list, onComposeTo }) {
    if (!list || list.length === 0) return null;
    return (
        <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start", py: 0.4 }}>
            <Typography sx={{
                fontSize: 12, fontWeight: 700, color: "#64748b",
                width: LABEL_W, mt: 0.5, flexShrink: 0,
            }}>
                {label}
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, flex: 1 }}>
                {list.map((a, i) => {
                    const display = formatAddress(a);
                    return (
                        <Tooltip key={`${a.address}-${i}`} title="이 주소로 메일 쓰기">
                            <Chip
                                size="small"
                                label={display}
                                clickable
                                onClick={() => onComposeTo?.(display)}
                                sx={{
                                    fontSize: 11.5, fontWeight: 500, height: 24,
                                    bgcolor: "#eff6ff", color: "#1e40af",
                                    "& .MuiChip-label": { px: 1 },
                                    "&:hover": { bgcolor: "#dbeafe" },
                                    cursor: "pointer",
                                }}
                            />
                        </Tooltip>
                    );
                })}
            </Box>
        </Box>
    );
}

/* ── 텍스트+아이콘 툴바 버튼 — ActionButton 기반, toolbar-style sx override ─ */
function ToolBtn({ icon, label, onClick, danger }) {
    return (
        <ActionButton
            variant="secondary"
            startIcon={icon}
            onClick={onClick}
            sx={{
                fontSize: 12.5, color: danger ? "#dc2626" : "#475569",
                fontWeight: 500, px: 1.2, py: 0.5, minWidth: "auto",
                borderColor: "transparent",
                "&:hover": { borderColor: "transparent", bgcolor: danger ? "#fee2e2" : "#f1f5f9", color: danger ? "#dc2626" : "#475569" },
            }}
            label={label}
        />
    );
}

export default function MailDetail({ mail, folder, onClose, onMove, onMoveToFolder, onCancelScheduled, onSendDraft, onEditDraft, customFolders, onDeleteForever, onReply, onForward, onToggleStar, onComposeTo, onRulesChanged, onRestore }) {
    const [moveAnchor, setMoveAnchor] = React.useState(null);
    const [addContactOpen, setAddContactOpen] = React.useState(false);
    const [previewAtt, setPreviewAtt] = useState(null);
    /* 차단 확인 다이얼로그 — {ruleType, pattern, label} 이면 열림 */
    const [blockDialog, setBlockDialog] = React.useState(null);
    const [blockRetro,  setBlockRetro]  = React.useState(false);
    const [blockSaving, setBlockSaving] = React.useState(false);

    const recipients = mail.recipients || [];
    const tos  = recipients.filter(r => r.type === "TO");
    const ccs  = recipients.filter(r => r.type === "CC");
    const bccs = recipients.filter(r => r.type === "BCC");

    /**
     * 현재 메일 발신자/도메인이 차단 규칙에 걸려 있는지 판정.
     * - mail 변경 시마다 listEffectiveRules 한 번만 호출 — 내 USER 규칙 + 관리자 GLOBAL 규칙을 함께 받는다.
     *   (USER 규칙만 보면 GLOBAL 로 이미 차단된 발신자에 중복 USER 규칙이 쌓인다.)
     * - ADDRESS 규칙은 정확히 발신자 주소와 일치, DOMAIN 규칙은 발신자 도메인과 일치.
     * - enabled=false 규칙은 무시.
     * - 차단/해제 액션 후 reload.
     */
    const fromAddr   = (mail.fromAddr || "").trim().toLowerCase();
    const fromDomain = fromAddr.includes("@") ? fromAddr.slice(fromAddr.indexOf("@") + 1) : "";
    const [myRules,     setMyRules]     = React.useState([]);
    const [globalRules, setGlobalRules] = React.useState([]);
    const reloadRules = React.useCallback(async () => {
        try {
            const res  = await mailApi.listEffectiveRules();
            const data = (res?.data ?? res) || {};
            setMyRules(Array.isArray(data.userRules)   ? data.userRules   : []);
            setGlobalRules(Array.isArray(data.globalRules) ? data.globalRules : []);
        } catch { setMyRules([]); setGlobalRules([]); }
    }, []);
    React.useEffect(() => { reloadRules(); }, [mail.id, reloadRules]);

    /** 규칙 목록에서 ruleType·패턴이 일치하는 활성 규칙 1건을 찾는다(값이 비면 매칭 없음). */
    const findRule = React.useCallback((list, ruleType, value) => (
        value
            ? list.find(r => r.enabled !== false && r.ruleType === ruleType
                && (r.pattern || "").trim().toLowerCase() === value)
            : undefined
    ), []);

    const blockingAddressRule = React.useMemo(() => findRule(myRules,     "ADDRESS", fromAddr),   [findRule, myRules,     fromAddr]);
    const blockingDomainRule  = React.useMemo(() => findRule(myRules,     "DOMAIN",  fromDomain), [findRule, myRules,     fromDomain]);
    const globalAddressRule   = React.useMemo(() => findRule(globalRules, "ADDRESS", fromAddr),   [findRule, globalRules, fromAddr]);
    const globalDomainRule    = React.useMemo(() => findRule(globalRules, "DOMAIN",  fromDomain), [findRule, globalRules, fromDomain]);
    const isAddressBlocked = !!blockingAddressRule;
    const isDomainBlocked  = !!blockingDomainRule;
    /** 관리자 전사 규칙으로 이미 차단된 발신자 — USER 규칙을 또 만들 필요가 없다(읽기 전용 안내만). */
    const globalBlockingRule = globalAddressRule || globalDomainRule;

    /**
     * 버튼 분기는 사이드바 folder prop 이 아니라 서버가 내려준 실제 폴더(mail.folder) 기준.
     * '전체'/검색 목록에서 이미 스팸함에 있는 메일을 열어도 '스팸 해제' 가 정확히 노출된다.
     * mail.folder 가 없는 응답(구버전 캐시 등)에서만 folder prop 으로 폴백.
     */
    const actualFolder  = mail.folder || folder;
    const isSpamFolder  = actualFolder === "SPAM";
    const isTrashFolder = actualFolder === "TRASH";
    /* 예약함 — 서버가 status=SCHEDULED 메일의 폴더 이동을 전부 거부하므로 '삭제' 대신 '예약 취소' 만 노출한다. */
    const isScheduledFolder = actualFolder === "SCHEDULED";
    /* 임시보관함 — 저장된 내용 그대로 발송하는 버튼을 노출한다. 자세한 배경은 그 버튼 주석 참조. */
    const isDraftFolder = actualFolder === "DRAFT";

    /** 차단 액션 노출 여부 — 수신 계열 폴더 + INBOUND 메일만. */
    const canBlockSender = !!fromAddr && mail.direction !== "OUTBOUND"
        && BLOCKABLE_FOLDERS.includes(actualFolder);
    /* 전사 차단이 걸려 있으면 새 USER 규칙 생성 버튼은 감추되, 이미 있는 내 규칙의 해제는 남겨둔다. */
    const showAddressBlockAction = canBlockSender && (isAddressBlocked || !globalBlockingRule);
    const showDomainBlockAction  = canBlockSender && (isDomainBlocked  || !globalBlockingRule);

    const Sep = () => <Divider orientation="vertical" flexItem sx={{ mx: 0.6, my: 0.6, borderColor: "#e2e8f0" }} />;

    const handleMarkUnread = async () => {
        try { await mailApi.markUnread(mail.id); onClose?.(); } catch { /* noop */ }
    };

    /**
     * 스팸 해제 / 휴지통 복원 — 이전 폴더(previousFolder) 로 **원위치 복원**.
     * <p>예전처럼 onMove(id, "INBOX") 로 하드코딩하면 원래 커스텀 폴더에 있던 메일이
     * 받은편지함으로 흘러들어간다. 서버 restore 는 previousFolder 가 없을 때만 INBOX 로 폴백한다.
     * onRestore prop 이 있으면 목록/카운트 갱신까지 부모가 처리하도록 위임.
     */
    const handleRestore = async () => {
        if (onRestore) { onRestore(mail.id); return; }
        try {
            await mailApi.restore(mail.id);
            notify("원래 폴더로 복원했습니다", "success");
            onClose?.();
        } catch (e) {
            // http 인터셉터가 서버 메시지를 Error.message 로 옮기고 response 객체는 버린다
            // (util/http.js — Promise.reject(new Error(getApiErrorMessage(error)))).
            // e.response.data.message 로 읽으면 항상 undefined 라 서버 사유가 사라진다.
            notify(e?.message || "복원 실패", "error");
        }
    };

    /**
     * 발신자/도메인 차단 다이얼로그 열기 — 실제 등록은 submitBlock().
     * 소급 적용은 되돌리기 부담이 커서 확인 다이얼로그의 체크박스(기본 off)로 받는다.
     */
    const openBlockDialog = (ruleType) => {
        if (!fromAddr) {
            notify("발신자 주소를 확인할 수 없습니다", "warning");
            return;
        }
        if (ruleType === "DOMAIN" && !fromDomain) {
            notify("도메인을 추출할 수 없습니다", "warning");
            return;
        }
        setBlockRetro(false);
        setBlockDialog({
            ruleType,
            pattern: ruleType === "DOMAIN" ? fromDomain : fromAddr,
            label:   ruleType === "DOMAIN" ? "이 도메인" : "이 발신자",
        });
    };

    /**
     * 차단 규칙 등록 — 백엔드는 USER 차단 규칙으로 저장.
     * <p>applyRetroactively 는 체크박스 값(기본 false) 그대로 전달하고,
     * 서버가 돌려준 retroMovedCount 를 토스트에 노출한다.
     * 본인 주소·사내 도메인 차단 등은 백엔드가 400 으로 거부하므로 서버 메시지를 그대로 보여준다.
     */
    const submitBlock = async () => {
        if (!blockDialog) return;
        const { ruleType, pattern, label } = blockDialog;
        setBlockSaving(true);
        try {
            const res  = await mailApi.createRule({
                ruleType,
                pattern,
                action: "SPAM",
                enabled: true,
                applyRetroactively: blockRetro,
                note: `MailDetail 에서 ${label} 차단`,
            });
            const moved = ((res?.data ?? res) || {}).retroMovedCount;
            notify(
                `${label}(${pattern}) 차단 규칙을 등록했습니다`
                + (moved ? ` — 기존 메일 ${moved}건을 스팸함으로 옮겼습니다` : ""),
                "success");
            setBlockDialog(null);
            await reloadRules();
            onRulesChanged?.();   // 목록/미확인 카운트 갱신은 부모가 담당
        } catch (e) {
            // 서버 400 사유("본인 메일 주소는 차단할 수 없습니다" 등)는 인터셉터가 Error.message 로 옮긴다.
            notify(e?.message || "차단 규칙 등록 실패", "error");
        } finally {
            setBlockSaving(false);
        }
    };

    /**
     * 차단 해제 — 매칭되는 USER 차단 규칙 1건을 soft delete.
     * 같은 발신자에 ADDRESS·DOMAIN 두 종류 차단이 모두 있을 수 있어 ruleType 별로 분리 호출.
     */
    const handleUnblock = async (rule, label) => {
        if (!rule) return;
        const ok = await confirm({
            title: "차단 해제",
            message: `${label} 차단을 해제하시겠습니까?`,
            confirmLabel: "해제",
        });
        if (!ok) return;
        try {
            await mailApi.deleteRule(rule.id);
            notify(`${label} 차단을 해제했습니다`, "success");
            await reloadRules();
            onRulesChanged?.();   // 해제 후에도 목록/카운트 재조회
        } catch (e) {
            // GLOBAL 규칙 id 를 개인 endpoint 로 보낸 경우의 403 메시지 등을 그대로 노출한다.
            notify(e?.message || "차단 해제 실패", "error");
        }
    };

    return (
        <Box sx={{ display: "flex", flexDirection: "column", height: "100%", bgcolor: "#fff" }}>
            {/* ── 제목 — 최상단, 진짜 제목처럼 ─ */}
            <Box sx={{
                px: 2.5, pt: 2, pb: 1.5, flexShrink: 0,
                borderBottom: "1px solid #eef2f6", bgcolor: "#fff",
            }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                    {mail.isStarred
                        ? <StarRoundedIcon       sx={{ fontSize: 22, color: "#f59e0b" }} />
                        : <StarBorderRoundedIcon sx={{ fontSize: 22, color: "#cbd5e1" }} />}
                    <Typography sx={{ fontSize: 20, fontWeight: 800, color: "#0f172a", flex: 1, lineHeight: 1.3 }}>
                        {mail.subject || "(제목 없음)"}
                    </Typography>
                </Stack>
            </Box>

            {/* ── 단일 툴바 (텍스트 + 아이콘 한 줄) — 제목 아래 ─ */}
            <Box sx={{
                display: "flex", alignItems: "center", flexWrap: "wrap", gap: 0.2,
                px: 1.5, py: 0.8, borderBottom: "1px solid #e2e8f0", bgcolor: "#fafbfc",
                flexShrink: 0,
            }}>
                {isScheduledFolder && onCancelScheduled && (
                    <ToolBtn icon={<ScheduleSendRoundedIcon sx={{ fontSize: 16 }} />} label="예약 취소"
                             onClick={() => onCancelScheduled(mail.id)} danger />
                )}
                {/* 임시보관함 전용 '편집' — 작성 창에 되돌려 이어서 쓴다.
                    서버가 내려주는 mail.editable 로만 판단한다. 조건을 여기서 재조립하면
                    이관분 판별에 필요한 rawS3Key·sesMessageId 가 이 DTO 에 없어 헐거워진다
                    (MailDetailDto#editable 주석에 조건과 근거가 있다). */}
                {isDraftFolder && onEditDraft && mail.editable && (
                    <ToolBtn icon={<EditRoundedIcon sx={{ fontSize: 16 }} />} label="편집"
                             onClick={() => onEditDraft(mail.id)} />
                )}
                {/* 임시보관함 전용 '발송' — 저장된 내용을 그대로 보낸다(편집 없이).
                    서버 API(POST /api/mail/{id}/send)와 프론트 함수(mailApi.send)는 원래 있었는데
                    화면 진입로가 없어 아무도 못 쓰던 기능이다. 그 탓에 발송 실패한 메일과 이관된
                    임시저장 메일이 임시보관함에 쌓이기만 하고 손댈 방법이 없었다.
                    editable=false 인 메일(이관분·발송 실패분)에는 이 버튼이 유일한 수단이다. */}
                {isDraftFolder && onSendDraft && (
                    <ToolBtn icon={<SendRoundedIcon sx={{ fontSize: 16 }} />} label="발송"
                             onClick={() => onSendDraft(mail.id)} />
                )}
                <ToolBtn icon={<ReplyRoundedIcon          sx={{ fontSize: 16 }} />} label="답장"     onClick={() => onReply(false)} />
                <ToolBtn icon={<ReplyAllRoundedIcon       sx={{ fontSize: 16 }} />} label="전체 회신" onClick={() => onReply(true)} />
                <ToolBtn icon={<ForwardRoundedIcon        sx={{ fontSize: 16 }} />} label="전달"     onClick={onForward} />

                <Sep />

                {/* 예약함에서는 삭제 버튼을 내린다 — 서버 이동 가드가 status=SCHEDULED 메일의 폴더 이동을
                    전부 거부하므로(폴더만 옮겨도 디스패처가 그대로 발송한다) 눌러도 항상 실패한다.
                    취소는 위의 '예약 취소' 가 담당한다. */}
                {isScheduledFolder ? null : isTrashFolder ? (
                    <ToolBtn icon={<DeleteForeverRoundedIcon sx={{ fontSize: 16 }} />} label="영구 삭제" onClick={() => onDeleteForever?.(mail.id)} danger />
                ) : (
                    <ToolBtn icon={<DeleteOutlineRoundedIcon sx={{ fontSize: 16 }} />} label="삭제"     onClick={() => onMove(mail.id, "TRASH")} danger />
                )}
                {/* 실제 폴더가 스팸함이면 스팸 해제(=원위치 복원), 그 외는 스팸 신고 */}
                {isSpamFolder
                    ? <ToolBtn icon={<MoveToInboxRoundedIcon sx={{ fontSize: 16 }} />} label="스팸 해제" onClick={handleRestore} />
                    : <ToolBtn icon={<ReportRoundedIcon       sx={{ fontSize: 16 }} />} label="스팸 신고" onClick={() => onMove(mail.id, "SPAM")} />}
                <ToolBtn icon={<MarkEmailUnreadRoundedIcon sx={{ fontSize: 16 }} />} label="안읽음" onClick={handleMarkUnread} />

                {/* 이동 메뉴 — ToolBtn 과 동일 toolbar-style ActionButton.
                    예약함에서는 통째로 숨긴다: 서버 이동 가드가 status=SCHEDULED 메일의 폴더 이동을
                    (커스텀 폴더 포함) 전부 거부하므로, 남겨두면 어느 항목을 눌러도 100% 실패하는 죽은 메뉴다.
                    예약을 풀려면 위의 '예약 취소'(→ 임시보관함) 를 쓴다. 툴바(MailPage)도 같은 기준으로 숨긴다. */}
                {!isScheduledFolder && <>
                <ActionButton variant="secondary"
                    startIcon={<DriveFileMoveRoundedIcon sx={{ fontSize: 16 }} />}
                    onClick={(e) => setMoveAnchor(e.currentTarget)}
                    sx={{
                        fontSize: 12.5, color: "#475569", fontWeight: 500,
                        px: 1.2, py: 0.5, minWidth: "auto", borderColor: "transparent",
                        "&:hover": { borderColor: "transparent", bgcolor: "#f1f5f9", color: "#475569" },
                    }}
                    label="이동" />
                <Menu open={Boolean(moveAnchor)} anchorEl={moveAnchor} onClose={() => setMoveAnchor(null)}>
                    <MenuItem onClick={() => { onMove(mail.id, "INBOX");   setMoveAnchor(null); }}>
                        <MoveToInboxRoundedIcon sx={{ fontSize: 16, mr: 1, color: "#64748b" }} /> 받은 메일함
                    </MenuItem>
                    <MenuItem onClick={() => { onMove(mail.id, "TRASH");   setMoveAnchor(null); }}>
                        <DeleteOutlineRoundedIcon sx={{ fontSize: 16, mr: 1, color: "#dc2626" }} /> 휴지통
                    </MenuItem>
                    {customFolders && customFolders.length > 0 && <Divider />}
                    {(customFolders || []).map(cf => (
                        <MenuItem key={cf.id}
                                  onClick={() => { onMoveToFolder?.(mail.id, cf.id); setMoveAnchor(null); }}>
                            <FolderRoundedIcon sx={{ fontSize: 16, mr: 1, color: cf.color || "#f59e0b" }} /> {cf.name}
                        </MenuItem>
                    ))}
                </Menu>
                </>}

                {/* 인쇄 — 이전엔 더보기 메뉴 안에 숨겨져 있었음. 사용자 요청으로 인라인 노출. */}
                <ToolBtn icon={<PrintRoundedIcon sx={{ fontSize: 16 }} />} label="인쇄" onClick={() => window.print()} />

                {/*
                  발신자/도메인 차단 — 차단 상태에 따라 인라인으로 분기 노출.
                  - 차단 안 된 상태: "발신자 차단" / "도메인 차단" 버튼
                  - 차단 된 상태: 빨간 칩(차단됨 표시) + "차단 해제" 버튼
                  - 관리자 전사(GLOBAL) 규칙으로 이미 차단된 발신자: 읽기 전용 안내만 (중복 USER 규칙 생성 방지)
                  ADDRESS / DOMAIN 두 종류는 독립적이라 각각 따로 보여준다.
                  수신 계열 폴더(BLOCKABLE_FOLDERS)가 아니면 전체를 숨긴다 — 보낸편지함에서 본인 차단 방지.
                */}
                {canBlockSender && globalBlockingRule && (
                    <Tooltip title="관리자가 등록한 전사 차단 규칙입니다. 해제하려면 관리자에게 문의하세요.">
                        <Chip
                            size="small"
                            icon={<BlockRoundedIcon sx={{ fontSize: 14, color: "#b45309 !important" }} />}
                            label={`관리자 전사 차단됨 (${globalAddressRule ? globalAddressRule.pattern : `@${globalDomainRule.pattern}`})`}
                            sx={{
                                ml: 0.5, height: 24, fontSize: 11.5, fontWeight: 700,
                                bgcolor: "#fef3c7", color: "#92400e",
                                border: "1px solid #fcd34d",
                                "& .MuiChip-icon": { ml: 0.6 },
                            }}
                        />
                    </Tooltip>
                )}
                {showAddressBlockAction && (isAddressBlocked ? (
                    <>
                        <Chip
                            size="small"
                            icon={<BlockRoundedIcon sx={{ fontSize: 14, color: "#dc2626 !important" }} />}
                            label={`발신자 차단됨${blockingAddressRule.pattern ? ` (${blockingAddressRule.pattern})` : ""}`}
                            sx={{
                                ml: 0.5, height: 24, fontSize: 11.5, fontWeight: 700,
                                bgcolor: "#fee2e2", color: "#b91c1c",
                                border: "1px solid #fecaca",
                                "& .MuiChip-icon": { ml: 0.6 },
                            }}
                        />
                        <ToolBtn
                            icon={<LockOpenRoundedIcon sx={{ fontSize: 16 }} />}
                            label="차단 해제"
                            onClick={() => handleUnblock(blockingAddressRule, "발신자")}
                        />
                    </>
                ) : (
                    <ToolBtn
                        icon={<BlockRoundedIcon sx={{ fontSize: 16 }} />}
                        label="발신자 차단"
                        onClick={() => openBlockDialog("ADDRESS")}
                    />
                ))}
                {showDomainBlockAction && (isDomainBlocked ? (
                    <>
                        <Chip
                            size="small"
                            icon={<DomainDisabledRoundedIcon sx={{ fontSize: 14, color: "#dc2626 !important" }} />}
                            label={`도메인 차단됨${blockingDomainRule.pattern ? ` (@${blockingDomainRule.pattern})` : ""}`}
                            sx={{
                                ml: 0.5, height: 24, fontSize: 11.5, fontWeight: 700,
                                bgcolor: "#fee2e2", color: "#b91c1c",
                                border: "1px solid #fecaca",
                                "& .MuiChip-icon": { ml: 0.6 },
                            }}
                        />
                        <ToolBtn
                            icon={<LockOpenRoundedIcon sx={{ fontSize: 16 }} />}
                            label="도메인 차단 해제"
                            onClick={() => handleUnblock(blockingDomainRule, "도메인")}
                        />
                    </>
                ) : (
                    <ToolBtn
                        icon={<DomainDisabledRoundedIcon sx={{ fontSize: 16 }} />}
                        label="도메인 차단"
                        onClick={() => openBlockDialog("DOMAIN")}
                    />
                ))}

                <Box sx={{ flex: 1 }} />

                <Tooltip title={mail.isStarred ? "별표 해제" : "별표"}>
                    <IconButton size="small" onClick={onToggleStar} sx={{ p: 0.6 }}>
                        {mail.isStarred
                            ? <StarRoundedIcon       sx={{ fontSize: 18, color: "#f59e0b" }} />
                            : <StarBorderRoundedIcon sx={{ fontSize: 18, color: "#94a3b8" }} />}
                    </IconButton>
                </Tooltip>
                <Tooltip title="닫기">
                    <IconButton size="small" onClick={onClose} sx={{ p: 0.6 }}>
                        <CloseRoundedIcon sx={{ fontSize: 18, color: "#64748b" }} />
                    </IconButton>
                </Tooltip>
            </Box>

            {/* ── 본문 영역 (스크롤) ─ */}
            <Box sx={{ flex: 1, overflowY: "auto", px: 2.5, py: 2 }}>
                {/* 메타 — 회색 배경/아바타 X. 라벨+값 한 줄씩. */}
                <Box sx={{ mb: 2 }}>
                    {/* 보낸이 — 클릭 시 그 주소로 메일 작성 */}
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ py: 0.4 }}>
                        <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#64748b", width: LABEL_W, flexShrink: 0 }}>
                            보낸이
                        </Typography>
                        <Tooltip title="이 보낸이에게 메일 쓰기">
                            <Chip
                                size="small"
                                clickable
                                onClick={() => onComposeTo?.(formatAddress({ displayName: mail.fromName, address: mail.fromAddr }))}
                                label={
                                    <Box component="span" sx={{ display: "inline-flex", alignItems: "baseline", gap: 0.6 }}>
                                        <Box component="span" sx={{ fontWeight: 700, color: "#0f172a" }}>
                                            {mail.fromName || mail.fromAddr}
                                        </Box>
                                        {mail.fromName && (
                                            <Box component="span" sx={{ fontSize: 11.5, color: "#64748b" }}>
                                                &lt;{mail.fromAddr}&gt;
                                            </Box>
                                        )}
                                    </Box>
                                }
                                sx={{
                                    fontSize: 12.5, height: 26, bgcolor: "#eff6ff",
                                    "& .MuiChip-label": { px: 1, color: "#1e40af" },
                                    "&:hover": { bgcolor: "#dbeafe" },
                                    cursor: "pointer",
                                }}
                            />
                        </Tooltip>
                        {mail.fromAddr && (
                            <Tooltip title="연락처에 추가">
                                <IconButton size="small" onClick={() => setAddContactOpen(true)}
                                    sx={{ p: 0.4, color: "#94a3b8", "&:hover": { color: "#2563eb", bgcolor: "#eff6ff" } }}>
                                    <PersonAddAltRoundedIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                            </Tooltip>
                        )}
                    </Stack>

                    <ContactEditDialog
                        open={addContactOpen}
                        initial={{ name: mail.fromName || "", email: mail.fromAddr || "" }}
                        onClose={() => setAddContactOpen(false)}
                        onSaved={() => setAddContactOpen(false)}
                    />

                    {/* 받는이 / 참조 / 숨은참조 */}
                    <AddressLine label="받는이"     list={tos}  onComposeTo={onComposeTo} />
                    <AddressLine label="참조"       list={ccs}  onComposeTo={onComposeTo} />
                    <AddressLine label="숨은 참조" list={bccs} onComposeTo={onComposeTo} />

                    {/* 보낸일시 */}
                    <Stack direction="row" alignItems="baseline" spacing={1} sx={{ py: 0.4 }}>
                        <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#64748b", width: LABEL_W, flexShrink: 0 }}>
                            보낸일시
                        </Typography>
                        <Typography sx={{ fontSize: 12, color: "#475569" }}>
                            {mail.receivedAt ? dayjs(mail.receivedAt).format("YYYY년 M월 D일 (ddd) HH:mm") : ""}
                        </Typography>
                    </Stack>

                    {/*
                      배달 상태 — 내가 보낸 메일에만.
                      "보냈다"(status=SENT, SES 접수)와 "닿았다"(deliveryStatus=DELIVERED)는 다른 축이라
                      보낸일시 바로 아래에 따로 둔다. 반송·스팸신고는 여기서 사유 원문까지 같이 보여준다
                      — 목록 배지는 tooltip 이라 사유를 읽으려면 마우스를 올려야 하는데, 상세까지 왔다면
                      이미 "왜 안 갔지?" 를 확인하러 온 사용자다.
                    */}
                    {DELIVERY_BADGE_FOLDERS.includes(mail.folder) && resolveDeliveryBadge(mail) && (
                        <Stack direction="row" alignItems="center" spacing={1} sx={{ py: 0.4 }}>
                            <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#64748b", width: LABEL_W, flexShrink: 0 }}>
                                배달상태
                            </Typography>
                            <DeliveryStatusBadge mail={mail} showHint />
                        </Stack>
                    )}

                    {/*
                      첨부 — 보낸일시 아래 메타 행으로. 본문 끝까지 스크롤해야 첨부가 있는지 알 수 있던
                      걸 헤더에서 바로 보이게 옮겼다(긴 메일일수록 차이가 크다).

                      본문에 inline 으로 박힌 이미지는 여기서 숨긴다 (Gmail / Outlook 동일 동작) —
                      본문에 이미 cid:/filename rewrite 로 그려져 있어 카드로 또 보여주면 중복이다.
                      사용자가 의도해서 붙인 파일만 (isInline=false) 카드로 노출.
                    */}
                    {(() => {
                        const visible = (mail.attachments || []).filter(a => !a.isInline);
                        if (visible.length === 0) return null;
                        return (
                            <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start", py: 0.4 }}>
                                <Typography sx={{
                                    fontSize: 12, fontWeight: 700, color: "#64748b",
                                    width: LABEL_W, flexShrink: 0,
                                }}>
                                    첨부 {visible.length}
                                </Typography>
                                {/* 항목 사이 간격은 항목 안 간격(0.75)보다 넓게 — 안 그러면 어디까지가
                                    한 파일인지 구분이 안 된다. */}
                                <Box sx={{
                                    display: "flex", flexWrap: "wrap",
                                    columnGap: 2.5, rowGap: 0.75, flex: 1, minWidth: 0,
                                }}>
                                    {visible.map((att) => (
                                        <AttachmentCard key={att.id} att={att} onPreview={setPreviewAtt} />
                                    ))}
                                </Box>
                            </Box>
                        );
                    })()}
                </Box>

                {/* 메타와 본문 사이 디바이더 */}
                <Divider sx={{ mb: 2.5, borderColor: "#e2e8f0" }} />

                {/*
                  외부 대용량 공유 링크 — WeTransfer / Naver MyBox / Google Drive 등.
                  발신자가 40MB+ 첨부를 외부 서비스로 보낸 경우 본문에 링크가 묻혀 있는데,
                  백엔드 ShareLinkExtractor 가 사전 추출하여 sharedLinks 로 내려줌.
                  본문 위에 강조 박스로 표시 — 사용자가 놓치지 않도록.
                */}
                {Array.isArray(mail.sharedLinks) && mail.sharedLinks.length > 0 && (
                    <Box sx={{
                        mb: 2.5, p: 1.5, borderRadius: 1.5,
                        bgcolor: "#fef3c7", border: "1px solid #fcd34d",
                    }}>
                        <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 1 }}>
                            <LinkRoundedIcon sx={{ fontSize: 16, color: "#92400e" }} />
                            <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: "#92400e" }}>
                                외부 공유 링크 {mail.sharedLinks.length}개 발견
                            </Typography>
                            <Typography sx={{ fontSize: 11, color: "#a16207", ml: 1 }}>
                                대용량 파일은 메일 첨부 대신 외부 공유 서비스로 전달된 경우가 많습니다.
                            </Typography>
                        </Stack>
                        <Stack spacing={0.5}>
                            {mail.sharedLinks.map((s, i) => (
                                <Box key={i} sx={{
                                    display: "flex", alignItems: "center", gap: 1,
                                    p: 0.75, bgcolor: "#fff", borderRadius: 1,
                                    border: "1px solid #fef3c7",
                                }}>
                                    <Chip
                                        label={s.service}
                                        size="small"
                                        sx={{
                                            fontSize: 10.5, height: 20,
                                            bgcolor: "#fbbf24", color: "#78350f", fontWeight: 700,
                                        }}
                                    />
                                    <Typography
                                        component="a"
                                        href={s.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        sx={{
                                            fontSize: 12, color: "#1d4ed8", flex: 1,
                                            wordBreak: "break-all", textDecoration: "none",
                                            "&:hover": { textDecoration: "underline" },
                                        }}
                                    >
                                        {s.url}
                                    </Typography>
                                    <OpenInNewRoundedIcon sx={{ fontSize: 13, color: "#94a3b8" }} />
                                </Box>
                            ))}
                        </Stack>
                    </Box>
                )}

                {/* 본문 — sanitize / cid·token rewrite / viewer CSS 는 MailBodyHtml 내부에 캡슐화 */}
                <MailBodyHtml
                    html={mail.bodyHtml}
                    mailId={mail.id}
                    attachments={mail.attachments}
                    fallbackText={mail.bodyText}
                />

                {/* 첨부 목록은 본문 아래가 아니라 헤더(보낸일시 아래)에 있다 — 위쪽 메타 영역 참고. */}

                <AttachmentPreviewModal
                    open={Boolean(previewAtt)}
                    attachment={previewAtt}
                    onClose={() => setPreviewAtt(null)}
                    onDownload={saveAttachment}
                />
            </Box>

            {/*
              차단 확인 다이얼로그 — 공통 confirm() 은 체크박스를 지원하지 않아 여기서만 직접 구성.
              "기존 메일에도 적용"(=applyRetroactively) 은 기본 off — 예전엔 항상 true 라
              차단 한 번에 과거 메일이 통째로 스팸함으로 사라졌다.
            */}
            <Dialog
                open={Boolean(blockDialog)}
                onClose={() => { if (!blockSaving) setBlockDialog(null); }}
                maxWidth="xs" fullWidth
                PaperProps={{ sx: { borderRadius: "12px" } }}
                transitionDuration={120}
            >
                <DialogTitle sx={{ fontSize: 15, fontWeight: 700, color: "#0f172a", pb: 1, pt: 2.5, px: 3 }}>
                    {blockDialog?.label} 차단
                </DialogTitle>
                <DialogContent sx={{ px: 3, pt: 0, pb: 1 }}>
                    <Typography sx={{ fontSize: 13.5, color: "#334155", lineHeight: 1.6 }}>
                        {blockDialog?.label}({blockDialog?.pattern}) 의 메일을 차단합니다.
                        <br />앞으로 도착하는 메일은 자동으로 스팸함으로 이동합니다.
                    </Typography>
                    <FormControlLabel
                        sx={{ mt: 1.5, alignItems: "flex-start" }}
                        control={
                            <Checkbox
                                size="small"
                                checked={blockRetro}
                                onChange={(e) => setBlockRetro(e.target.checked)}
                                sx={{ py: 0.3 }}
                            />
                        }
                        label={
                            <Typography sx={{ fontSize: 12.5, color: "#475569", lineHeight: 1.5, mt: 0.4 }}>
                                기존 메일에도 적용 — 이미 받은 같은 발신자 메일도 스팸함으로 옮깁니다
                            </Typography>
                        }
                    />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5, pt: 1, gap: 1 }}>
                    <ActionButton
                        variant="cancel"
                        onClick={() => setBlockDialog(null)}
                        disabled={blockSaving}
                        label="취소"
                        sx={{ minWidth: 80 }}
                    />
                    <ActionButton
                        variant="confirm"
                        onClick={submitBlock}
                        loading={blockSaving}
                        startIcon={false}
                        label="차단"
                        sx={{
                            minWidth: 80,
                            bgcolor: "#dc2626",
                            "&:hover": { bgcolor: "#b91c1c" },
                        }}
                    />
                </DialogActions>
            </Dialog>
        </Box>
    );
}
